import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button,
  Tabs, TabList, TabPanels, Tab, TabPanel, VStack, HStack, Box, Text, Input, Select,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, useToast, Badge, Spinner, Divider,
  FormControl, FormLabel, SimpleGrid, Icon, Link, Textarea,
} from '@chakra-ui/react';
import { FiTrash2, FiUpload, FiFile, FiPlus, FiRefreshCw } from 'react-icons/fi';
import deportesApi, {
  Torneo, Equipo, Encuentro, ArchivoGenerico, TablaPosiciones, FilaTabla,
} from '../../../api/deportesApi';
import { ROJO, ROJO_HOVER, fmtFecha, API_UPLOADS } from '../_shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  torneo: Torneo | null;
}

const ModalGestionTorneo: React.FC<Props> = ({ isOpen, onClose, torneo }) => {
  const toast = useToast();
  const archivoRef = useRef<HTMLInputElement>(null);

  const [equipos, setEquipos] = useState<Equipo[]>([]);
  const [encuentros, setEncuentros] = useState<Encuentro[]>([]);
  const [archivos, setArchivos] = useState<ArchivoGenerico[]>([]);
  const [tabla, setTabla] = useState<TablaPosiciones | null>(null);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  // nuevo equipo
  const [nvEquipo, setNvEquipo] = useState({ nombre: '', entrenadorRepresentante: '', contacto: '' });
  // nuevo encuentro
  const [nvEnc, setNvEnc] = useState<any>({ equipoLocalId: '', equipoVisitanteId: '', jornada: '', fecha: '', hora: '', sede: '' });
  // captura de resultado por encuentro
  const [resTmp, setResTmp] = useState<Record<number, { gl: string; gv: string }>>({});

  const cargar = async () => {
    if (!torneo) return;
    setCargando(true);
    try {
      const [eq, en, ar] = await Promise.all([
        deportesApi.listarEquipos(torneo.id),
        deportesApi.listarEncuentros(torneo.id),
        deportesApi.listarTorneoArchivos(torneo.id),
      ]);
      setEquipos(eq || []);
      setEncuentros(en || []);
      setArchivos(ar || []);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (isOpen && torneo) { cargar(); setTabla(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, torneo]);

  const nombreEquipo = (id?: number | null) => equipos.find((e) => e.id === id)?.nombre || '—';

  // ─── Equipos ───
  const addEquipo = async () => {
    if (!torneo || !nvEquipo.nombre) { toast({ title: 'Nombre del equipo requerido', status: 'warning', duration: 2500 }); return; }
    try {
      await deportesApi.crearEquipo(torneo.id, nvEquipo);
      setNvEquipo({ nombre: '', entrenadorRepresentante: '', contacto: '' });
      toast({ title: 'Equipo agregado', status: 'success', duration: 2000 });
      cargar();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };
  const delEquipo = async (id: number) => {
    try { await deportesApi.eliminarEquipo(id); toast({ title: 'Equipo eliminado', status: 'success', duration: 2000 }); cargar(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  // ─── Encuentros ───
  const addEncuentro = async () => {
    if (!torneo || !nvEnc.equipoLocalId || !nvEnc.equipoVisitanteId) {
      toast({ title: 'Selecciona ambos equipos', status: 'warning', duration: 2500 }); return;
    }
    if (nvEnc.equipoLocalId === nvEnc.equipoVisitanteId) {
      toast({ title: 'Los equipos deben ser distintos', status: 'warning', duration: 2500 }); return;
    }
    try {
      await deportesApi.crearEncuentro(torneo.id, {
        equipoLocalId: Number(nvEnc.equipoLocalId),
        equipoVisitanteId: Number(nvEnc.equipoVisitanteId),
        jornada: nvEnc.jornada ? Number(nvEnc.jornada) : null,
        fecha: nvEnc.fecha || null,
        hora: nvEnc.hora || null,
        sede: nvEnc.sede || null,
      });
      setNvEnc({ equipoLocalId: '', equipoVisitanteId: '', jornada: '', fecha: '', hora: '', sede: '' });
      toast({ title: 'Encuentro agregado', status: 'success', duration: 2000 });
      cargar();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };
  const delEncuentro = async (id: number) => {
    try { await deportesApi.eliminarEncuentro(id); toast({ title: 'Encuentro eliminado', status: 'success', duration: 2000 }); cargar(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };
  const guardarResultado = async (enc: Encuentro) => {
    const t = resTmp[enc.id];
    if (!t || t.gl === '' || t.gv === '') { toast({ title: 'Captura ambos marcadores', status: 'warning', duration: 2500 }); return; }
    try {
      await deportesApi.registrarResultado(enc.id, { golesLocal: Number(t.gl), golesVisitante: Number(t.gv) });
      toast({ title: 'Resultado registrado', status: 'success', duration: 2000 });
      cargar();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  // ─── Tabla de posiciones ───
  const calcularTabla = async () => {
    if (!torneo) return;
    setCargando(true);
    try { setTabla(await deportesApi.obtenerTablaPosiciones(torneo.id)); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
    finally { setCargando(false); }
  };

  // ─── Archivos / Evidencias ───
  const subirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !torneo) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      await deportesApi.subirTorneoArchivo(torneo.id, fd);
      toast({ title: 'Archivo subido', status: 'success', duration: 2000 });
      cargar();
    } catch (err: any) { toast({ title: 'Error', description: err?.response?.data?.message, status: 'error', duration: 3000 }); }
    finally { setSubiendo(false); if (archivoRef.current) archivoRef.current.value = ''; }
  };
  const delArchivo = async (id: number) => {
    try { await deportesApi.eliminarTorneoArchivo(id); toast({ title: 'Archivo eliminado', status: 'success', duration: 2000 }); cargar(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  const esImagen = (a: ArchivoGenerico) => (a.mimeType || '').startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes((a.tipoArchivo || '').toLowerCase());

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>
          Gestión de Torneo — {torneo?.nombre}
        </ModalHeader>
        <ModalBody py={4}>
          {cargando && !equipos.length ? (
            <VStack py={8}><Spinner color={ROJO} /></VStack>
          ) : (
            <Tabs colorScheme="red" variant="enclosed">
              <TabList>
                <Tab>Equipos ({equipos.length})</Tab>
                <Tab>Encuentros ({encuentros.length})</Tab>
                <Tab>Tabla de posiciones</Tab>
                <Tab>Evidencias ({archivos.length})</Tab>
              </TabList>
              <TabPanels>
                {/* ── EQUIPOS ── */}
                <TabPanel px={0}>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2} mb={3}>
                    <Input size="sm" placeholder="Nombre del equipo *" value={nvEquipo.nombre} onChange={(e) => setNvEquipo({ ...nvEquipo, nombre: e.target.value })} />
                    <Input size="sm" placeholder="Entrenador / representante" value={nvEquipo.entrenadorRepresentante} onChange={(e) => setNvEquipo({ ...nvEquipo, entrenadorRepresentante: e.target.value })} />
                    <Input size="sm" placeholder="Contacto" value={nvEquipo.contacto} onChange={(e) => setNvEquipo({ ...nvEquipo, contacto: e.target.value })} />
                    <Button size="sm" leftIcon={<FiPlus />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={addEquipo}>Agregar</Button>
                  </SimpleGrid>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg={ROJO}><Tr><Th color="white">Equipo</Th><Th color="white">Entrenador</Th><Th color="white">Contacto</Th><Th color="white"></Th></Tr></Thead>
                      <Tbody>
                        {equipos.length === 0 ? (
                          <Tr><Td colSpan={4} textAlign="center" py={6}><Text color="gray.400">Sin equipos</Text></Td></Tr>
                        ) : equipos.map((e) => (
                          <Tr key={e.id}>
                            <Td fontWeight="medium">{e.nombre}</Td>
                            <Td>{e.entrenadorRepresentante || '—'}</Td>
                            <Td>{e.contacto || '—'}</Td>
                            <Td><IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" colorScheme="red" variant="ghost" onClick={() => delEquipo(e.id)} /></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* ── ENCUENTROS ── */}
                <TabPanel px={0}>
                  <Box p={3} bg="gray.50" borderRadius="md" mb={3}>
                    <Text fontSize="xs" fontWeight="bold" color={ROJO} mb={2}>NUEVO ENCUENTRO</Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                      <Select size="sm" placeholder="Equipo local" value={nvEnc.equipoLocalId} onChange={(e) => setNvEnc({ ...nvEnc, equipoLocalId: e.target.value })}>
                        {equipos.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                      </Select>
                      <Select size="sm" placeholder="Equipo visitante" value={nvEnc.equipoVisitanteId} onChange={(e) => setNvEnc({ ...nvEnc, equipoVisitanteId: e.target.value })}>
                        {equipos.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                      </Select>
                      <Input size="sm" type="number" placeholder="Jornada" value={nvEnc.jornada} onChange={(e) => setNvEnc({ ...nvEnc, jornada: e.target.value })} />
                      <Input size="sm" type="date" value={nvEnc.fecha} onChange={(e) => setNvEnc({ ...nvEnc, fecha: e.target.value })} />
                      <Input size="sm" type="time" value={nvEnc.hora} onChange={(e) => setNvEnc({ ...nvEnc, hora: e.target.value })} />
                      <Input size="sm" placeholder="Sede" value={nvEnc.sede} onChange={(e) => setNvEnc({ ...nvEnc, sede: e.target.value })} />
                    </SimpleGrid>
                    <Button size="sm" mt={2} leftIcon={<FiPlus />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={addEncuentro} isDisabled={equipos.length < 2}>
                      Agregar encuentro
                    </Button>
                    {equipos.length < 2 && <Text fontSize="xs" color="orange.500" mt={1}>Registra al menos 2 equipos.</Text>}
                  </Box>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg={ROJO}>
                        <Tr>
                          <Th color="white">J</Th><Th color="white">Fecha</Th><Th color="white">Local</Th>
                          <Th color="white">Marcador</Th><Th color="white">Visitante</Th>
                          <Th color="white">Estatus</Th><Th color="white">Registrar resultado</Th><Th color="white"></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {encuentros.length === 0 ? (
                          <Tr><Td colSpan={8} textAlign="center" py={6}><Text color="gray.400">Sin encuentros</Text></Td></Tr>
                        ) : encuentros.map((en) => {
                          const fin = en.estatus === 'Finalizado';
                          const tmp = resTmp[en.id] || { gl: en.golesLocal != null ? String(en.golesLocal) : '', gv: en.golesVisitante != null ? String(en.golesVisitante) : '' };
                          return (
                            <Tr key={en.id}>
                              <Td>{en.jornada ?? '—'}</Td>
                              <Td fontSize="xs">{fmtFecha(en.fecha)} {en.hora || ''}</Td>
                              <Td>{en.equipoLocal?.nombre || nombreEquipo(en.equipoLocalId)}</Td>
                              <Td textAlign="center" fontWeight="bold">{fin ? `${en.golesLocal} - ${en.golesVisitante}` : '—'}</Td>
                              <Td>{en.equipoVisitante?.nombre || nombreEquipo(en.equipoVisitanteId)}</Td>
                              <Td><Badge colorScheme={fin ? 'green' : 'yellow'}>{en.estatus}</Badge></Td>
                              <Td>
                                <HStack spacing={1}>
                                  <Input size="xs" w="45px" type="number" value={String(tmp.gl)} onChange={(e) => setResTmp({ ...resTmp, [en.id]: { ...tmp, gl: e.target.value } })} />
                                  <Text fontSize="xs">-</Text>
                                  <Input size="xs" w="45px" type="number" value={String(tmp.gv)} onChange={(e) => setResTmp({ ...resTmp, [en.id]: { ...tmp, gv: e.target.value } })} />
                                  <Button size="xs" colorScheme="green" onClick={() => guardarResultado(en)}>OK</Button>
                                </HStack>
                              </Td>
                              <Td><IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" colorScheme="red" variant="ghost" onClick={() => delEncuentro(en.id)} /></Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* ── TABLA DE POSICIONES ── */}
                <TabPanel px={0}>
                  <HStack mb={3} justify="space-between">
                    <Text fontSize="sm" color="gray.500">
                      {torneo?.calculaPosiciones ? 'Tabla calculada desde encuentros finalizados.' : 'Tabla en modo MANUAL.'}
                    </Text>
                    <Button size="sm" leftIcon={<FiRefreshCw />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={calcularTabla}>
                      Calcular tabla
                    </Button>
                  </HStack>
                  {tabla ? (
                    <>
                      <Badge colorScheme={tabla.origen === 'CALCULADA' ? 'blue' : 'purple'} mb={2}>Origen: {tabla.origen}</Badge>
                      <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                          <Thead bg={ROJO}>
                            <Tr>
                              <Th color="white">#</Th><Th color="white">Equipo</Th>
                              <Th color="white" isNumeric>PJ</Th><Th color="white" isNumeric>PG</Th><Th color="white" isNumeric>PE</Th><Th color="white" isNumeric>PP</Th>
                              <Th color="white" isNumeric>GF</Th><Th color="white" isNumeric>GC</Th><Th color="white" isNumeric>DIF</Th><Th color="white" isNumeric>PTS</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {tabla.tabla.length === 0 ? (
                              <Tr><Td colSpan={10} textAlign="center" py={6}><Text color="gray.400">Sin datos para la tabla</Text></Td></Tr>
                            ) : tabla.tabla.map((r: FilaTabla, i: number) => (
                              <Tr key={r.equipoId} bg={i === 0 ? 'green.50' : undefined}>
                                <Td fontWeight="bold">{i + 1}</Td>
                                <Td>{r.equipo}</Td>
                                <Td isNumeric>{r.pj}</Td><Td isNumeric>{r.pg}</Td><Td isNumeric>{r.pe}</Td><Td isNumeric>{r.pp}</Td>
                                <Td isNumeric>{r.gf}</Td><Td isNumeric>{r.gc}</Td><Td isNumeric>{r.dif}</Td>
                                <Td isNumeric fontWeight="bold" color={ROJO}>{r.pts}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </>
                  ) : (
                    <Box py={8} textAlign="center"><Text color="gray.400">Presiona "Calcular tabla" para generar las posiciones.</Text></Box>
                  )}
                </TabPanel>

                {/* ── EVIDENCIAS ── */}
                <TabPanel px={0}>
                  <input ref={archivoRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={subirArchivo} />
                  <Button size="sm" leftIcon={<FiUpload />} variant="outline" colorScheme="red" mb={3} onClick={() => archivoRef.current?.click()} isLoading={subiendo}>
                    Subir archivo (imagen o PDF)
                  </Button>
                  <Divider mb={3} />
                  {archivos.length === 0 ? (
                    <Box py={6} textAlign="center"><Text color="gray.400">Sin evidencias</Text></Box>
                  ) : (
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                      {archivos.map((a) => (
                        <Box key={a.id} borderWidth="1px" borderRadius="md" p={2} position="relative">
                          <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" colorScheme="red" position="absolute" top={1} right={1} onClick={() => delArchivo(a.id)} />
                          {esImagen(a) ? (
                            <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal>
                              <img src={`${API_UPLOADS}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4 }} />
                            </Link>
                          ) : (
                            <VStack py={4}><Icon as={FiFile} boxSize={7} color="red.500" /></VStack>
                          )}
                          <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal fontSize="2xs" color="blue.500" noOfLines={1}>{a.nombreArchivo}</Link>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalGestionTorneo;
